import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '../ui/button';
import { heroAssets } from '../../mock';

const Hero = ({ t, language, scrollToSection }) => {
  const [heroPhase, setHeroPhase] = useState('video');
  const [showContent, setShowContent] = useState(false);
  const [imagePreloaded, setImagePreloaded] = useState(false);

  // Preload static image for smooth transition
  useEffect(() => {
    const img = new Image();
    img.src = heroAssets.staticImage;
    img.onload = () => setImagePreloaded(true);
  }, []);

  // Force video autoplay on mobile devices
  useEffect(() => {
    if (heroPhase === 'video') {
      const video = document.querySelector('.hero-video');
      if (video) {
        video.play().catch(() => {});
      }
    }
  }, [heroPhase]);

  // Hero video transition — crossfade video out, reveal image behind
  useEffect(() => {
    const transitionTimer = setTimeout(() => {
      setHeroPhase('static');
      setTimeout(() => setShowContent(true), 300);
    }, 7000);
    return () => clearTimeout(transitionTimer);
  }, []);

  const handleScrollDown = useCallback(() => {
    scrollToSection('intro');
  }, [scrollToSection]);

  const handleCtaClick = useCallback(() => {
    scrollToSection('contact');
  }, [scrollToSection]);

  return (
    <section id="hero" className="hero-section" aria-label={language === 'cs' ? 'Úvod' : 'Hero'}>
      {/* Static image layer (always present, behind video) */}
      <div className="hero-image-container">
        <img 
          src={heroAssets.staticImage} 
          alt={language === 'cs' ? 'UrbanCage modulární multisportovní hřiště – snímek z výšky' : 'UrbanCage modular multi-sport court - aerial view'} 
          className="hero-image"
          width="1920"
          height="1080"
        />
        <div className="hero-overlay"></div>
      </div>

      {/* Video layer (fades out on transition) */}
      <div className={`hero-video-container ${heroPhase !== 'video' ? 'fade-out' : ''}`}>
        <video
          autoPlay
          muted
          playsInline
          preload="auto"
          className="hero-video"
          aria-hidden="true"
        >
          <source src={heroAssets.video} type="video/mp4" />
        </video>
      </div>

      {/* Content appears after transition */}
      <div className={`hero-content ${showContent ? 'show' : ''}`}>
        <h1 className="visually-hidden">
          {language === 'cs' ? 'UrbanCage\u00AE - Modulární multisportovní hřiště a klece na fotbal a basketbal' :
           'UrbanCage\u00AE - Modular Multi-Sport Courts and Football Basketball Cages'}
        </h1>
        <img 
          src={heroAssets.logo} 
          alt="UrbanCage Logo" 
          className="hero-logo"
          width="400"
          height="100"
        />
        <p className="hero-slogan">{t.heroSlogan}</p>
        <Button 
          className="cta-button"
          onClick={handleCtaClick}
          aria-label={language === 'cs' ? 'Odeslat poptávku' : 'Send inquiry'}
          data-testid="hero-cta-btn"
        >
          {t.ctaRequestInquiry}
        </Button>
      </div>

      {/* Scroll down arrow */}
      <button 
        className={`scroll-down-arrow ${showContent ? 'show' : ''}`}
        onClick={handleScrollDown}
        aria-label={language === 'cs' ? 'Posunout dolů' : 'Scroll down'}
        data-testid="scroll-down-arrow"
      >
        <ChevronDown size={28} />
      </button>
    </section>
  );
};

export default React.memo(Hero);
